import React from "react";
import { Box, Typography } from "@mui/material";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import SportsKabaddi from "@mui/icons-material/SportsKabaddi";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTranslation } from "react-i18next";
import { SIGNUP_FONT } from "../utils/signupPageStyles";
import { BRAND_PRIMARY } from "../constants/brandColors";
import { getMm } from "../theme/modernMaterialEnterprise";

const ROLE_OPTIONS = [
  {
    value: "Parent",
    labelKey: "signup_i_am_parent",
    hintKey: "signup_role_parent_hint",
    icons: [SupervisorAccountIcon],
  },
  {
    value: "Student",
    labelKey: "signup_i_am_student",
    hintKey: "signup_role_student_hint",
    icons: [SportsKabaddi],
  },
  {
    value: "student&Parent",
    labelKey: "signup_i_am_both",
    hintKey: "signup_role_both_hint",
    icons: [SupervisorAccountIcon, SportsKabaddi],
  },
];

function SignupRoleOption({ option, selected, isDarkMode, onSelect, t }) {
  const hintColor = selected
    ? "rgba(255, 255, 255, 0.88)"
    : getMm(isDarkMode).onSurfaceVariant;

  return (
    <Box
      component="button"
      type="button"
      onClick={() => onSelect(option.value)}
      aria-pressed={selected}
      sx={{
        appearance: "none",
        WebkitAppearance: "none",
        m: 0,
        width: "100%",
        minHeight: 88,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "center",
        gap: 0.5,
        p: { xs: 2, sm: 2.25 },
        textAlign: "left",
        cursor: "pointer",
        borderRadius: "8px",
        border: "2px solid",
        borderColor: selected ? BRAND_PRIMARY.dark : getMm(isDarkMode).outlineVariant,
        backgroundColor: selected ? BRAND_PRIMARY.main : getMm(isDarkMode).surfaceLowest,
        color: selected ? "#FFFFFF" : getMm(isDarkMode).onSurface,
        fontFamily: SIGNUP_FONT,
        position: "relative",
        boxShadow: "none",
        transition: "background-color 0.15s ease, border-color 0.15s ease",
        "&:hover": {
          borderColor: selected ? BRAND_PRIMARY.dark : getMm(isDarkMode).outline,
          backgroundColor: selected
            ? BRAND_PRIMARY.dark
            : getMm(isDarkMode).surfaceContainer,
          boxShadow: "none",
        },
        "&:focus-visible": {
          outline: `2px solid ${BRAND_PRIMARY.focusRing}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: option.icons.length > 1 ? 0.25 : 0,
            color: selected ? "#FFFFFF" : getMm(isDarkMode).onSurfaceVariant,
            flexShrink: 0,
          }}
        >
          {option.icons.map((Icon, idx) => (
            <Icon key={idx} sx={{ fontSize: option.icons.length > 1 ? 22 : 26 }} />
          ))}
        </Box>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { xs: "0.9375rem", sm: "1rem" },
            lineHeight: 1.3,
            fontFamily: SIGNUP_FONT,
            flex: 1,
          }}
        >
          {t(option.labelKey)}
        </Typography>
        {selected ? (
          <CheckCircleIcon sx={{ fontSize: 22, color: "#FFFFFF", flexShrink: 0 }} />
        ) : null}
      </Box>
      <Typography
        sx={{
          fontSize: "0.8125rem",
          lineHeight: 1.45,
          color: hintColor,
          fontFamily: SIGNUP_FONT,
          pl: { xs: 3.5, sm: 4.75 },
        }}
      >
        {t(option.hintKey)}
      </Typography>
    </Box>
  );
}

export default function SignupRoleSelector({ role, onSelect, isDarkMode }) {
  const { t } = useTranslation();

  return (
    <Box sx={{ width: "100%", mb: { xs: 2, sm: 0 } }}>
      <Typography
        sx={{
          textAlign: "center",
          fontWeight: 700,
          fontSize: { xs: "1.125rem", sm: "1.25rem" },
          color: getMm(isDarkMode).onSurface,
          mb: 0.75,
          fontFamily: SIGNUP_FONT,
        }}
      >
        {t("signup_role_select_heading")}
      </Typography>
      <Typography
        sx={{
          textAlign: "center",
          fontSize: "0.875rem",
          color: getMm(isDarkMode).onSurfaceVariant,
          mb: 2,
          fontFamily: SIGNUP_FONT,
        }}
      >
        {t("signup_role_select_subheading")}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
        }}
      >
        {ROLE_OPTIONS.map((option) => (
          <SignupRoleOption
            key={option.value}
            option={option}
            selected={role === option.value}
            isDarkMode={isDarkMode}
            onSelect={onSelect}
            t={t}
          />
        ))}
      </Box>
    </Box>
  );
}
